import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  Divider,
} from "@mui/material";
import { ExportMenu } from "../../../shared/ExportMenu";
import { PolicyOrAccount } from "../../../interfaces/DTO/mislaka_xml_models";
import { formatCurrency } from "../../../utils/namesFormater";
import {
  formatContributionType,
  formatLiquidityBalanceComponent,
  formatLiquidityLayerScopeCode,
  formatLiquidityMatchedDepositCap,
  formatLiquidityPeriodBalanceType,
  formatPolicyTypeCategory,
} from "../../../utils/enumFormatter";

interface AccumulatedSavingsTableProps {
  policy: PolicyOrAccount; // אובייקט פוליסה
}

const AccumulatedSavingsTable: React.FC<AccumulatedSavingsTableProps> = ({
  policy,
}) => {
  const {
    liquidity_details,
    liquidity_summaries,
    accumulated_savings,
    policy_number,
    program_name,
  } = policy;

  // סיכום סה"כ חיסכון לפי קוד סוג הפקדה
  const savingsByDepositType = accumulated_savings.reduce<Record<number, number>>(
    (acc, item) => {
      acc[item.deposit_type_code] = (acc[item.deposit_type_code] || 0) + item.total_savings;
      return acc;
    },
    {}
  );

  const accumulatedSavingsSummary = Object.entries(savingsByDepositType).map(
    ([depositTypeCode, totalSavings]) => ({
      deposit_type_code: Number(depositTypeCode),
      total_savings: totalSavings,
    })
  );

  // Format data for export
  const formattedAccumulatedSavings = accumulated_savings.map((item) => ({
    "Savings Type": formatLiquidityPeriodBalanceType(item.savings_type_code),
    "Deposit Type": formatContributionType(item.deposit_type_code),
    "Total Savings": formatCurrency(item.total_savings),
    "Total Redemption Value": formatCurrency(item.total_redemption_value),
  }));

  const formattedLiquidityDetails = liquidity_details.map((item) => ({
    "Start Period Code": formatLiquidityLayerScopeCode(item.start_period_code),
    "Matched Deposit Cap": formatLiquidityMatchedDepositCap(
      item.matched_deposit_cap
    ),
    "Balance Component": formatLiquidityBalanceComponent(
      item.balance_component
    ),
    "Balance Type": formatLiquidityPeriodBalanceType(item.balance_type),
    "Balance Amount": formatCurrency(item.balance_amount),
  }));

  const formattedLiquiditySummaries = liquidity_summaries.map((item) => ({
    "Liquidity Date": item.liquidity_date,
    "Liquidity Amount": formatCurrency(item.liquidity_amount),
  }));

  return (
    <Box sx={{ p: 4 }} dir="rtl">
      {/* <Typography variant="h6" gutterBottom>
        נתוני נזילות וחיסכון
      </Typography> */}

      {/* פרטי הפוליסה */}
      {/* <Box sx={{ mb: 4 }}>
        <Typography variant="h6">מספר פוליסה: {policy_number}</Typography>
        <Typography variant="h6">שם תוכנית: {program_name}</Typography>
      </Box> */}

      {/* סיכום סה"כ חיסכון לפי קוד סוג הפקדה */}
      <Card variant="outlined" sx={{ mb: 4, p: 2 }}>
        <Box display="flex" gap={4} alignItems="center" flexWrap="wrap">
          {accumulatedSavingsSummary.map((item, index) => (
            <React.Fragment key={item.deposit_type_code}>
              {index > 0 && <Divider orientation="vertical" flexItem />}
              <Box textAlign="center">
                <Typography variant="h6" component="div">
                  {formatCurrency(item.total_savings)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatContributionType(item.deposit_type_code)}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Card>

      {/* חיסכון נצבר */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ flexGrow: 1 }}>
          חיסכון נצבר
        </Typography>
        <ExportMenu
          data={formattedAccumulatedSavings}
          filename={`accumulated_savings_${policy_number}`}
        />
      </Box>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>קוד סוג חיסכון</TableCell>
              <TableCell>קוד סוג הפקדה</TableCell>
              <TableCell>סה"כ חיסכון</TableCell>
              <TableCell>סה"כ ערכי פדיון</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accumulated_savings.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  {formatLiquidityPeriodBalanceType(item.savings_type_code)}
                </TableCell>
                <TableCell>
                  {formatContributionType(item.deposit_type_code)}
                </TableCell>
                <TableCell>{formatCurrency(item.total_savings)}</TableCell>
                <TableCell>
                  {formatCurrency(item.total_redemption_value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* פרטי נזילות */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ flexGrow: 1 }}>
          פרטי נזילות
        </Typography>
        <ExportMenu
          data={formattedLiquidityDetails}
          filename={`liquidity_details_${policy_number}`}
        />
      </Box>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>קוד תחילת תקופה</TableCell>
              <TableCell>תקרת הפקדה מותאמת</TableCell>
              <TableCell>רכיב יתרה</TableCell>
              <TableCell>סוג יתרה</TableCell>
              <TableCell>סכום יתרה</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liquidity_details.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  {formatLiquidityLayerScopeCode(item.start_period_code)}
                </TableCell>
                <TableCell>
                  {formatLiquidityMatchedDepositCap(item.matched_deposit_cap)}
                </TableCell>
                <TableCell>
                  {formatLiquidityBalanceComponent(item.balance_component)}
                </TableCell>
                <TableCell>
                  {formatLiquidityPeriodBalanceType(item.balance_type)}
                </TableCell>
                <TableCell>{formatCurrency(item.balance_amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* סיכום נזילות */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ flexGrow: 1 }}>
          סיכום נזילות
        </Typography>
        <ExportMenu
          data={formattedLiquiditySummaries}
          filename={`liquidity_summaries_${policy_number}`}
        />
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>תאריך נזילות</TableCell>
              <TableCell>סכום נזילות</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liquidity_summaries.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.liquidity_date}</TableCell>
                <TableCell>{formatCurrency(item.liquidity_amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AccumulatedSavingsTable;
